import { Btn } from 'components/Btn'
import { Icon } from 'components/Icon'
import { RT } from 'components/RT'
import { isEmpty, log, useState } from 'utils'
import React, { JSXElementConstructor, ReactElement, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { ClipBoardStyleProps, clipBoardStyles } from './Clipboard.styles'

const ClipboardView = styled.div`
	${clipBoardStyles}
`

export type ClipboardProps = ClipBoardStyleProps & {
	children: string | ReactElement<any, string | JSXElementConstructor<any>>
	className?: string
	badgeTime?: number
	btnDisplay?: 'icon-only' | 'no-icon' | 'full'
	copyBtnText?: string
	hidePreview?: boolean
	selectOnCopy?: boolean
	onAfterCopy?: () => void
}
export const Clipboard = ({
	className,
	children,
	badgeTime = 2000,
	copyBtnText = 'Copy',
	btnDisplay = 'full',
	hidePreview = false,
	selectOnCopy = true,
	hideBorder = false,
	height = '100px',
	onAfterCopy,
}: ClipboardProps) => {
	const [copied, setCopied] = useState(false, 'copied')
	const contentElement = React.useRef<HTMLDivElement>(null)

	/**
	 *  Copy Handler
	 * */
	const copyHandler = useCallback(async () => {
		const el = contentElement.current

		if (!el) return

		const range = document.createRange()
		const selection = document.getSelection()

		if (navigator?.clipboard?.write) {
			/**
			 *  We can use future browser api
			 * */

			if (selectOnCopy) {
				selection?.removeAllRanges()
				range.selectNodeContents(el)
				selection?.addRange(range)
			}

			const clipboardData = {
				'text/plain': new Blob([el.innerText], { type: 'text/plain' }),
				'text/html': new Blob([el.innerHTML], { type: 'text/html' }),
				'text/rtf': new Blob([el.innerHTML], { type: 'text/rtf' }),
			}

			// Runs recursively in the case where one of the mime types is not supported and fails
			const copyToCB = () => {
				if (!isEmpty(clipboardData))
					navigator.clipboard
						.write([new ClipboardItem(clipboardData as any)])
						.then(() => {
							setCopied(true)
							log.ok('Copied to clipboard: ', el.innerHTML)
							onAfterCopy && onAfterCopy()
						})
						.catch(err => {
							if (err.name === 'NotAllowedError') {
								// parse the unsupported mime type from the error:
								// see: https://blog.tomayac.com/2020/03/20/multi-mime-type-copying-with-the-async-clipboard-api/
								const disallowedMimeType = err.message.replace(
									/^.*?\s(\w+\/[^\s]+).*?$/,
									'$1'
								) as keyof typeof clipboardData

								// Remove that mime type from the clipboard data
								delete clipboardData[disallowedMimeType]

								// try again
								copyToCB()
							} else log.err(err)
						})
			}

			copyToCB()
		} else if (document?.execCommand) {
			/**
			 *  We fall back to deprecated browser api
			 * */

			selection?.removeAllRanges()

			const copyEventHandler = (e: ClipboardEvent) => {
				const el = contentElement.current
				e.stopPropagation()
				e.preventDefault()
				e.clipboardData?.clearData()
				el && e.clipboardData?.setData('text/html', el.innerHTML)
			}

			if (hidePreview) {
				const tmp = document.createElement('div')
				tmp.style.position = 'fixed'
				tmp.style.top = '0'
				tmp.style.zIndex = '-2'
				tmp.innerHTML = el.innerHTML

				document.body.appendChild(tmp)

				tmp.addEventListener('copy', copyEventHandler)
				range.selectNodeContents(tmp)
			} else {
				el.addEventListener('copy', copyEventHandler)
				range.selectNodeContents(el)
			}

			selection?.addRange(range)
			const success = document.execCommand('copy')

			if (!selectOnCopy) selection?.removeAllRanges()
			if (!hidePreview) el.removeEventListener('copy', copyEventHandler)
			if (success) setCopied(true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (copied)
			setTimeout(() => {
				setCopied(false)
			}, badgeTime)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [copied])

	const iconOnly = btnDisplay === 'icon-only'
	const noIcon = btnDisplay === 'no-icon'

	return (
		<ClipboardView
			className={`clipboard${className ? ` ${className}` : ''}${iconOnly ? ' __icon-only' : ''}${
				hidePreview ? ' __no-preview' : ''
			}`}
			height={height}
			hideBorder={hideBorder}
		>
			<div className='copy-success'>
				<div
					className='slide-drawer'
					style={{
						transform: hidePreview
							? `translate3d(${copied ? '0' : '100%'}, 0, 0)`
							: `translate3d(0,${copied ? '0' : '-100%'},0)`,
					}}
				>
					Copied to clipboard!
				</div>
			</div>

			<div className='copy-preview' style={{ display: hidePreview ? 'none' : 'block' }}>
				<RT>
					<div ref={contentElement}>{children}</div>
				</RT>
			</div>

			<Btn className='copy-btn' onClick={copyHandler}>
				{noIcon ? null : <Icon type='clipboard' />} {iconOnly ? null : copyBtnText || 'Copy'}
			</Btn>
		</ClipboardView>
	)
}
